import React from 'react'
import cx from 'classnames'
import { elementType, string } from 'prop-types'

import { escapeRegExp } from '../../utils/escape-regexp'

const propTypes = {
    text: string,
    keyword: string,
    className: string,
    WrapperComponent: elementType,
    sectionClassName: string,
    highlightClassName: string
}

const defaultProps = {
    WrapperComponent: 'span'
}

/**
 * Highlight Component - Renders text with highlighted portions based on a specified string.
 *
 * @component
 *
 * @param {Object} props - The component's props.
 * @param {string} props.className - The CSS class for the surrounding Component.
 * @param {string} props.WrapperComponent - The component that surrounds the string - defaults to span.
 * @param {string} props.highlightClassName - The CSS class for the highlighted portion.
 * @param {string} props.keyword - The string to be highlighted within the text.
 * @param {string} props.sectionClassName - The CSS class for non-highlighted portions.
 * @param {string} props.text - The text to be rendered with highlights.
 *
 * @returns {React.Fragment} The rendered component with highlighted portions.
 *
 * @example
 * // Example usage of the Highlight component
 * <Highlight
 *   highlightClassName="highlighted-text"
 *   keyword="search"
 *   sectionClassName="normal-text"
 *   text="This is a sample text to search."
 * />
 */
export const Highlight = ({ WrapperComponent, className, highlightClassName, keyword, sectionClassName, text }) => {
    if (typeof text !== 'string') {
        return null
    }
    const lowerCaseText = text.toLocaleLowerCase()
    if (!keyword?.length || !lowerCaseText.includes(keyword.toLocaleLowerCase())) {
        return <WrapperComponent className={className}>{text}</WrapperComponent>
    }

    const result = Array.from(lowerCaseText.matchAll(new RegExp(`(${escapeRegExp(keyword)})`, 'gi')))
    let lastSectionEndIndex = 0
    const highlightedArray = result.reduce((list, section, index) => {
        if (lastSectionEndIndex < section.index) {
            list.push(
                <span className={sectionClassName} key={`start_${index}`}>
                    {text.slice(lastSectionEndIndex, section.index)}
                </span>
            )
        }

        const sectionEndIndex = section.index + section[0].length
        list.push(
            <em key={`highlight_${index}`} className={cx('t3sel-highlight-string', highlightClassName)}>
                {text.slice(section.index, sectionEndIndex)}
            </em>
        )

        lastSectionEndIndex = sectionEndIndex
        return list
    }, [])

    if (lastSectionEndIndex < text.length - 1) {
        highlightedArray.push(
            <span className={sectionClassName} key="end">
                {text.slice(lastSectionEndIndex)}
            </span>
        )
    }

    return <WrapperComponent className={className}>{highlightedArray}</WrapperComponent>
}

Highlight.propTypes = propTypes
Highlight.defaultProps = defaultProps
